if (!customElements.get('quick-add-toggle')) {
  customElements.define(
    'quick-add-toggle',
    class QuickAddToggle extends HTMLElement {
      constructor() {
        super();
        this.addButton = this.querySelector('.quick-add-toggle__button');
        this.quantityWrapper = this.querySelector('.quick-add-toggle__quantity');
        this.input = this.quantityWrapper.querySelector('.quantity__input');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.variantId = this.dataset.id;

        this.addButton.addEventListener('click', this.onAddButtonClick.bind(this));

        const debouncedOnChange = debounce((event) => {
          this.onQuantityChange(event);
        }, ON_CHANGE_DEBOUNCE_TIMER);
        this.quantityWrapper.addEventListener('change', debouncedOnChange.bind(this));
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'quick-add-toggle') return;
          this.syncFromCart(event.cartData);
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      }

      // Keep this card's state correct if the cart changed elsewhere (cart drawer, another card, etc.)
      syncFromCart(cartData) {
        if (!cartData || !Array.isArray(cartData.items)) return;
        const line = cartData.items.find((item) => item.variant_id === parseInt(this.variantId));
        const quantity = line ? line.quantity : 0;

        this.input.value = quantity;
        this.input.dataset.cartQuantity = quantity;

        if (quantity === 0) {
          this.showButton();
        } else {
          this.showQuantity();
        }
      }

      showQuantity() {
        this.addButton.classList.add('hidden');
        this.quantityWrapper.classList.remove('hidden');
      }

      showButton() {
        this.quantityWrapper.classList.add('hidden');
        this.addButton.classList.remove('hidden');
      }

      setButtonLoading(loading) {
        this.addButton.classList.toggle('loading', loading);
        const spinner = this.addButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !loading);
        this.addButton.toggleAttribute('aria-disabled', loading);
        this.isLoading = loading;
      }

      onAddButtonClick(event) {
        event.preventDefault();
        if (this.isLoading) return;

        this.setButtonLoading(true);

        const body = JSON.stringify({
          id: this.variantId,
          quantity: 1,
          sections: this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : ['cart-icon-bubble'],
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), ...{ body } })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'quick-add-toggle',
                productVariantId: this.variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              return;
            }

            this.input.value = 1;
            this.input.dataset.cartQuantity = 1;
            this.showQuantity();

            if (this.cart) {
              try {
                this.cart.renderContents(response);
              } catch (e) {
                // Some cart notification/drawer implementations expect the just-added
                // item's markup to already be present in this same response, which can
                // race and throw. That's a cosmetic popup-preview issue at worst — it
                // shouldn't block the icon bubble update below.
                console.error('Cart popup render failed, continuing with icon update:', e);
              }
            }
            // The 'sections' HTML bundled in the /cart/add.js response can lag by one
            // request, so refetch the icon bubble on its own to guarantee it's current.
            this.refreshCartIconBubble();

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'quick-add-toggle',
              productVariantId: this.variantId,
              cartData: response,
            });
          })
          .catch((e) => console.error(e))
          .finally(() => this.setButtonLoading(false));
      }

      onQuantityChange(event) {
        const previousQuantity = this.input.dataset.cartQuantity;
        const newQuantity = parseInt(this.input.value);

        this.quantityWrapper.classList.add('quantity__input-disabled');

        const body = JSON.stringify({
          id: this.variantId,
          quantity: newQuantity,
          sections: this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : ['cart-icon-bubble'],
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_change_url}`, { ...fetchConfig('javascript'), ...{ body } })
          .then((response) => response.text())
          .then((state) => {
            const parsedState = JSON.parse(state);
            this.quantityWrapper.classList.remove('quantity__input-disabled');

            if (parsedState.description || parsedState.errors) {
              this.input.value = previousQuantity;
              event.target.setCustomValidity(parsedState.description || '');
              event.target.reportValidity();
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'quick-add-toggle',
                productVariantId: this.variantId,
                errors: parsedState.errors || parsedState.description,
                message: parsedState.message,
              });
              return;
            }

            this.input.dataset.cartQuantity = newQuantity;

            if (newQuantity === 0) this.showButton();

            this.renderSections(parsedState);
            this.refreshCartIconBubble();

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'quick-add-toggle',
              productVariantId: this.variantId,
              cartData: parsedState,
            });
          })
          .catch((e) => {
            console.error(e);
            this.quantityWrapper.classList.remove('quantity__input-disabled');
          });
      }

      // Reads the cart straight from /cart.js — the canonical, always-current cart data
      // endpoint — and patches the icon bubble directly. We avoid relying on the 'sections'
      // HTML from /cart/add.js or /cart/change.js (or even a follow-up section-render
      // request) because that can momentarily reflect the cart state from just before the
      // write commits, making the bubble look "one add behind" on the very first add.
      refreshCartIconBubble() {
        fetch(`${routes.cart_url}.js`)
          .then((response) => response.json())
          .then((cart) => this.updateCartIconBubble(cart))
          .catch((e) => console.error(e));
      }

      updateCartIconBubble(cart) {
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (!cartIconBubble) return;

        let bubble = cartIconBubble.querySelector('.cart-count-bubble');

        if (!cart.item_count) {
          if (bubble) bubble.remove();
          return;
        }

        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          cartIconBubble.appendChild(bubble);
        }

        bubble.innerHTML = '';

        if (cart.item_count < 100) {
          const countSpan = document.createElement('span');
          countSpan.setAttribute('aria-hidden', 'true');
          countSpan.textContent = cart.item_count;
          bubble.appendChild(countSpan);
        }

        const hiddenSpan = document.createElement('span');
        hiddenSpan.className = 'visually-hidden';
        hiddenSpan.textContent = `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`;
        bubble.appendChild(hiddenSpan);
      }

      // Refresh cart icon / drawer contents in place, without popping the drawer open
      // (the drawer only opens automatically the first time an item is added).
      renderSections(parsedState) {
        if (!this.cart || !parsedState.sections) return;

        this.cart.getSectionsToRender().forEach((section) => {
          if (!parsedState.sections[section.id]) return;
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!sectionElement) return;
          sectionElement.innerHTML = this.cart.getSectionInnerHTML(
            parsedState.sections[section.id],
            section.selector
          );
        });
      }
    }
  );
}
